import { useMemo, useSyncExternalStore } from 'react';
import { BrandKit } from '@goldcast/api/backend';
import { deriveFontFormat } from '@goldcast/player-config/src/utils/fontUtils';
import { getFileNameFromPath, getFileNameWithoutExtension } from '@goldcast/player-config/src/utils/fileUtils';
import type { PlatformFontEntry } from '@goldcast/player-config/src/types/brand-kit-types';
import { getBrandKitUrl } from '@/Pages/BrandKit/components/utils';
import { platformBrandKitStore, selectPlatformBrandKit } from '@/stores/brandKit';

export interface UsePlatformBrandKitsReturn {
  brandKits: BrandKit[];
  selectedBrandKitId: string | undefined;
  selectedBrandKit: BrandKit | undefined;
  isLoading: boolean;
  error: string | undefined;
  selectBrandKit: (id: string) => void;
  ftuxFontEntries: PlatformFontEntry[];
}

export default function usePlatformBrandKits(): UsePlatformBrandKitsReturn {
  const { availableBrandKits, selectedBrandKitId, isLoadingBrandKits, loadError, ftuxBrandKit } = useSyncExternalStore(
    platformBrandKitStore.subscribe,
    platformBrandKitStore.getSnapshot
  );

  const kits = availableBrandKits as BrandKit[];
  const defaultKitId = selectedBrandKitId ?? kits.find(kit => kit.is_default)?.id ?? kits[0]?.id;
  const selectedBrandKit = kits.find(kit => kit.id === defaultKitId);

  const ftuxFontEntries: PlatformFontEntry[] = useMemo(
    () =>
      ((ftuxBrandKit?.fonts as string[] | undefined) ?? []).map(fontPath => ({
        url: getBrandKitUrl(fontPath),
        name: getFileNameWithoutExtension(getFileNameFromPath(fontPath)).replace(/,/g, ''),
        format: deriveFontFormat(fontPath)
      })),
    [ftuxBrandKit]
  );

  return {
    brandKits: kits,
    selectedBrandKitId: defaultKitId,
    selectedBrandKit,
    isLoading: isLoadingBrandKits,
    error: loadError,
    selectBrandKit: selectPlatformBrandKit,
    ftuxFontEntries
  };
}
